import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Truck } from "lucide-react";

import { AutoApplyToggle } from "@/components/AutoApplyToggle";
import { api, type SupplierAutomation } from "@/lib/api";

export function SuppliersAutoApply(): JSX.Element {
  const qc = useQueryClient();
  const suppliers = useQuery<SupplierAutomation[]>({
    queryKey: ["suppliers-automation"],
    queryFn: api.suppliersAutomation,
  });

  const toggle = useMutation({
    mutationFn: ({ slug, auto_apply }: { slug: string; auto_apply: boolean }) =>
      api.setSupplierAutomation(slug, { auto_apply }),
    onSuccess: (result) => {
      qc.setQueryData<SupplierAutomation[]>(["suppliers-automation"], (current) =>
        (current ?? []).map((row) => (row.slug === result.slug ? result : row)),
      );
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["suppliers-automation"] });
    },
  });

  const rows = suppliers.data ?? [];

  return (
    <section className="card-stack overflow-hidden">
      <div className="flex items-center gap-2 border-b border-line bg-paper-2 px-4 py-3">
        <Truck
          className="h-4 w-4 text-ink-3"
          strokeWidth={1.6}
          aria-hidden="true"
        />
        <h2 className="text-md font-semibold text-foreground">
          Auto-apply per fornitore
        </h2>
      </div>

      <div className="px-4 py-4">
        <p className="mb-4 max-w-2xl text-sm text-ink-3">
          Interruttore generale: l&apos;auto-apply scrive prezzi solo per i fornitori accesi qui
          e solo sui marketplace con l&apos;auto-apply acceso.
        </p>

        {suppliers.isLoading && <p className="text-sm text-ink-3">Caricamento…</p>}
        {suppliers.isError && (
          <p className="text-sm text-destructive">
            Errore nel caricamento fornitori.
          </p>
        )}

        <ul className="divide-y divide-line">
          {rows.map((row) => (
            <li
              key={row.slug}
              className="flex items-center justify-between py-2"
            >
              <span className="text-md font-medium text-foreground">
                {row.label}
              </span>
              <AutoApplyToggle
                value={row.auto_apply}
                onChange={(next) =>
                  toggle.mutate({ slug: row.slug, auto_apply: next })
                }
                marketplaceLabel={row.label}
                ariaLabel={`Auto-apply ${row.label}`}
                confirmBody={
                  <p className="mt-2 text-sm text-ink-3">
                    Confermo di voler abilitare l&apos;auto-apply per i prodotti{" "}
                    <strong>{row.label}</strong> su tutti i marketplace con
                    auto-apply attivo. I prezzi verranno scritti senza ulteriore
                    conferma.
                  </p>
                }
              />
            </li>
          ))}
        </ul>

        {toggle.isError && (
          <div className="mt-3 rounded-[var(--radius-lg)] border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            Errore nel salvataggio del fornitore. Riprova.
          </div>
        )}

        <p className="mt-3 text-xs text-ink-3">
          ⓘ Prodotti senza fornitore noto: esclusi dall&apos;auto-apply.
        </p>
      </div>
    </section>
  );
}
